import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import DestinationSelector, {
  type DestinationOption,
} from "@/components/DestinationSelector";

export interface NewTripData {
  name: string;
  destinations: DestinationOption[];
  isGroup: boolean;
  groupName: string;
}

export const EMPTY_NEW_TRIP: NewTripData = {
  name: "",
  destinations: [],
  isGroup: false,
  groupName: "",
};

interface NewTripFieldsProps {
  value: NewTripData;
  onChange: (patch: Partial<NewTripData>) => void;
  disabled?: boolean;
  idPrefix?: string;
}

/**
 * Inline "Create a new trip" form fields, shared between the New Invoice
 * flow (`TripSection`) and the Move Invoice dialog so the UX is identical
 * everywhere a trip is created alongside an invoice operation.
 */
export default function NewTripFields({
  value,
  onChange,
  disabled,
  idPrefix = "new-trip",
}: NewTripFieldsProps) {
  const groupCheckboxId = `${idPrefix}-is-group`;
  return (
    <div className="space-y-4 rounded-md border p-4 bg-muted/20">
      <div>
        <Label htmlFor={`${idPrefix}-name`}>Trip Name *</Label>
        <Input
          id={`${idPrefix}-name`}
          placeholder="e.g. Caribbean Family Cruise 2026"
          value={value.name}
          disabled={disabled}
          onChange={(e) => onChange({ name: e.target.value })}
        />
      </div>

      <div className="space-y-2">
        <Label>Destinations *</Label>
        <DestinationSelector
          value={value.destinations}
          onChange={(destinations) => { if (disabled) return; onChange({ destinations }); }}
          disabled={disabled}
          placeholder="Search regions, countries, U.S. states..."
        />
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id={groupCheckboxId}
          checked={value.isGroup}
          disabled={disabled}
          onCheckedChange={(checked) =>
            onChange({
              isGroup: checked === true,
              groupName: checked ? value.groupName : "",
            })
          }
        />
        <label
          htmlFor={groupCheckboxId}
          className="text-sm font-medium leading-none"
        >
          Group Trip
        </label>
      </div>

      {value.isGroup && (
        <div>
          <Label htmlFor={`${idPrefix}-group-name`}>Group Booking Number</Label>
          <Input
            id={`${idPrefix}-group-name`}
            placeholder="Group booking number"
            value={value.groupName}
            disabled={disabled}
            onChange={(e) => onChange({ groupName: e.target.value })}
          />
        </div>
      )}
    </div>
  );
}
